import { ActionContext } from 'vuex';
import { IAmenities } from '@llws/lift-entity-interfaces';
import amenities from '@/shared/services/amenities';
import {
  IApiResponse,
  IFilterPayloadV2,
  IMetaResponse,
  IObjectWithTranslations,
  IRemovePayload,
} from '@/shared';
import { saveTranslationsInObjects } from '@/shared/services/translations';

export interface IAmenitiesState {
  list?: IApiResponse<IAmenities[], IMetaResponse>;
  single?: IApiResponse<IAmenities, null>;
}

export default {
  namespaced: true, // 'amenities/'
  state: {
    list: undefined,
    single: undefined,
  },
  getters: {
    list(state: any): any[] {
      return [ ...state.list?.data || [] ];
    },

    grouped(state: any): { group: string, items: IAmenities[] }[] {
      const groups: { group: string, items: IAmenities[] }[] = [];

      if (state.list?.data || [].length > 0) {
        state.list.data.forEach((a: IAmenities) => {
          let idx = groups.findIndex((g: any) => g.group === a.classification);
          if (idx === -1) {
            groups.push({ group: a.classification!, items: [] });
            idx = groups.length - 1;
          }

          groups[idx].items.push(a);
        });
      }

      return groups.sort((a, b) => (a.group > b.group ? 1 : -1));
    },

    groupedUnitAmenities(state: any): { group: string, items: IAmenities[] }[] {
      const groups: { group: string, items: IAmenities[] }[] = [];

      if (state.list?.data || [].length > 0) {
        state.list.data.forEach((a: IAmenities) => {
          const idx = groups.findIndex((g: any) => (
            g.group === a.classification
          ));

          if (a.category === 'suite' && a.classification !== 'imported') {
            if (idx === -1) {
              groups.push({ group: a.classification!, items: [a] });
            } else {
              groups[idx].items.push(a);
            }
          }
        });
      }

      return groups
        .map((g) => ({ ...g, items: g.items.sort((a, b) => (a.name > b.name ? 1 : -1)) }))
        .sort((a, b) => (a.group > b.group ? 1 : -1));
    },

    groupedPropertyAmenities(state: any): { group: string, items: IAmenities[] }[] {
      const groups: { group: string, items: IAmenities[] }[] = [];

      if (state.list?.data || [].length > 0) {
        state.list.data.forEach((a: IAmenities) => {
          const idx = groups.findIndex((g: any) => (
            g.group === a.classification
          ));

          if (a.category === 'property' && a.classification !== 'imported') {
            if (idx === -1) {
              groups.push({ group: a.classification!, items: [a] });
            } else {
              groups[idx].items.push(a);
            }
          }
        });
      }
      return groups
        .map((g) => ({ ...g, items: g.items.sort((a, b) => (a.name > b.name ? 1 : -1)) }))
        .sort((a, b) => (a.group > b.group ? 1 : -1));
    },

    groupedCommercialAmenities(state: any): { group: string, items: IAmenities[]}[] {
      const groups: { group: string, items: IAmenities[] }[] = [];

      if (state.list?.data || [].length > 0) {
        state.list.data.forEach((a: IAmenities) => {
          const idx = groups.findIndex((g: any) => (
            g.group === a.classification
          ));

          if (a.category === 'commercial' && a.classification !== 'imported') {
            if (idx === -1) {
              groups.push({ group: a.classification!, items: [a] });
            } else {
              groups[idx].items.push(a);
            }
          }
        });
      }
      return groups
        .map((g) => ({ ...g, items: g.items.sort((a, b) => (a.name > b.name ? 1 : -1)) }))
        .sort((a, b) => (a.group > b.group ? 1 : -1));
    },

    groupedImportedAmenities(state: any): { group: string, items: IAmenities[] }[] {
      const groups: { group: string, items: IAmenities[] }[] = [];

      if (state.list?.data || [].length > 0) {
        state.list.data.forEach((a: IAmenities) => {
          const idx = groups.findIndex((g) => (
            g.group === a.classification
          ));

          if (a.classification === 'imported') {
            if (idx === -1) {
              groups.push({ group: a.classification, items: [a] });
            } else {
              groups[idx].items.push(a);
            }
          }
        });
      }

      return groups
        .map((g) => ({ ...g, items: g.items.sort((a, b) => (a.name > b.name ? 1 : -1)) }))
        .sort((a, b) => (a.group > b.group ? 1 : -1));
    },
  },
  mutations: {
    set: (state: any, payload: IApiResponse<IAmenities[], IMetaResponse>) => {
      state.list = payload;
    },
    reset: (state: IAmenitiesState) => {
      state.list = undefined;
      state.single = undefined;
    },
  },
  actions: {
    get: async (ctx: ActionContext<any, any>, payload: IFilterPayloadV2) => {
      if (!ctx.state.list) {
        const result = await amenities.getCustomAndDefaultAmenities(payload);
        ctx.commit('set', result);
      }
    },

    create: async (ctx: ActionContext<any, any>, {
      clientId,
      amenitiesToAdd,
    }: {
      clientId: number,
      amenitiesToAdd: IAmenities[],
    }) => {
      const result = await amenities.createCustomAmenity(clientId, amenitiesToAdd);
      const current = ctx.state.list;

      if (current) {
        current.data = [ ...current.data, ...result.data as Record<string, any>[] ];
        ctx.commit('set', current);
      }

      return result.data;
    },

    delete: async (ctx: ActionContext<any, any>, payload: IRemovePayload) => {
      await amenities.deleteCustomAmenity(payload.parentId!, payload.id);
      const current: IApiResponse<IAmenities[], IMetaResponse> = ctx.state.list;
      ctx.commit('set', { meta: current.meta, data: current.data?.filter((a) => a.id !== payload.id) });
    },

    saveTranslations: async (ctx: ActionContext<any, any>, data: IObjectWithTranslations<IAmenities>[]) => {
      const result = await saveTranslationsInObjects(data, 'amenities');
      ctx.commit('set', { ...ctx.state.list, ...{ data: result } });
    },
  },
};
